import { Plus, Trash2 } from "lucide-react";
import { inputCls } from "./ui";
import { ImageField } from "./ImageField";
import { ICONS, DynIcon } from "../lib/icons";

const isImg = (k) => k === "image" || k.endsWith("_image");

const IconPicker = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-3">
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg border border-[#D4AF37]/40 text-[#D4AF37]"><DynIcon name={value} size={18} /></span>
    <select value={ICONS[value] ? value : ""} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
      {!ICONS[value] && <option value="">Choose icon…</option>}
      {Object.keys(ICONS).map((n) => <option key={n} value={n}>{n}</option>)}
    </select>
  </div>
);

const label = (k) => String(k).replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " ");
const blank = (sample) => (typeof sample === "string" ? "" : Object.fromEntries(Object.keys(sample).map((k) => [k, Array.isArray(sample[k]) ? [] : ""])));

const Str = ({ value, onChange, path }) =>
  value.length > 70 ? (
    <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${path}`} />
  ) : (
    <input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`field-${path}`} />
  );

export function FieldTree({ value, onChange, path = "root" }) {
  if (typeof value === "string" || typeof value === "number") return <Str value={String(value)} onChange={onChange} path={path} />;
  if (Array.isArray(value)) {
    const set = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
    return (
      <div className="space-y-3">
        {value.map((item, i) => (
          <div key={i} className="flex gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
            <span className="pt-2 text-xs text-zinc-600">{i + 1}</span>
            <div className="min-w-0 flex-1"><FieldTree value={item} onChange={(v) => set(i, v)} path={`${path}-${i}`} /></div>
            <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="self-start p-2 text-zinc-600 hover:text-red-400" aria-label="Remove" data-testid={`remove-${path}-${i}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {value.length > 0 && (
          <button type="button" onClick={() => onChange([...value, blank(value[0])])} data-testid={`add-${path}`}
            className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4AF37] hover:text-[#F3D77A]"><Plus size={14} /> Add item</button>
        )}
      </div>
    );
  }
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {Object.entries(value).map(([k, v]) => (
        <div key={k} className={typeof v === "string" && v.length <= 70 && !isImg(k) ? "" : "sm:col-span-2"}>
          <span className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{label(k)}</span>
          {isImg(k) && typeof v === "string" ? (
            <div className="max-w-sm"><ImageField value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} testid={`img-${path}-${k}`} /></div>
          ) : k === "icon" && typeof v === "string" ? (
            <IconPicker value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} testid={`icon-${path}`} />
          ) : (
            <FieldTree value={v} onChange={(nv) => onChange({ ...value, [k]: nv })} path={`${path}-${k}`} />
          )}
        </div>
      ))}
    </div>
  );
}
